import { useEffect, useState } from 'react'
import { faArrowsRotate, faRightFromBracket } from '@fortawesome/free-solid-svg-icons'
import { onAuthStateChanged, signOut, User } from 'firebase/auth'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import theme from '~/design'
import useAPI from '~/prix/react/hooks/api'
import { auth } from '~/utils/client/auth'
import { version } from '~/utils/client/publicConfig'
import { bypassAuth } from '~/utils/shared/platform'
import AvatarHeader from '../avatarHeader'
import DrawerNavItem from './drawerNavItem'
import { GeneralDrawerItems, ProfileDrawerItems, RegisterDrawerItems } from './drawer.items'
import { canListUsers } from '~/packages/general/user/userManagementPermissions'

const Container = styled.div`
  display: flex;
  flex: 1;
  min-height: 100%;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${theme.colors.lightText};
`

const Content = styled.div`
  padding-top: ${theme.padding(2)};
`

const Title = styled.h2`
  font-size: 0.7rem;
  color: ${({ theme }) => theme.colors.darkGrey};
  font-weight: 500;
  text-transform: uppercase;
  user-select: none;
  letter-spacing: 1px;
`

const Footer = styled.footer`
  display: flex;
  justify-content: center;
`

interface ManagementSidebarProps {
  onNavigate?: () => void
}

export default function ManagementSidebar({ onNavigate }: ManagementSidebarProps) {
  const navigate = useNavigate()
  const { context } = useAPI()
  const [userInformation, setUserInformation] = useState<User | null>(null)
  const actorRole = context.user?.activeClient?.roles?.[0]
  const visibleProfileDrawerItems = ProfileDrawerItems.filter(
    item => item.href !== '/app/users' || canListUsers(actorRole),
  )

  useEffect(() => {
    if (bypassAuth) {
      return
    }

    const unsubscribe = onAuthStateChanged(auth, user => {
      setUserInformation(user)
    })

    return () => unsubscribe()
  }, [])

  const handleNavigate = () => {
    onNavigate?.()
  }

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        onNavigate?.()
        navigate('/')
      })
      .catch(error => {
        console.log('Erro ao sair', error)
      })
  }

  const handleReload = async () => {
    if (caches) {
      try {
        const keyList = await caches.keys()
        await Promise.all(keyList.map(key => caches.delete(key)))
      } catch (error) {
        console.log('Erro ao deletar caches', error)
      }
    }

    try {
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations()
        for (const registration of registrations) {
          await registration.unregister()
        }
      }
    } catch (error) {
      console.log('Erro ao retirar registro de service workers', error)
    }

    onNavigate?.()
    window.document.location.href = `/app/questionnaire/list?refresh=${Math.random()}`
  }

  return (
    <Container>
      <main>
        <Content>
          <AvatarHeader
            displayName={context.user?.displayName ?? userInformation?.displayName ?? undefined}
            email={context.user?.email ?? userInformation?.email ?? null}
          />
          <Title>Cadastro</Title>
          {RegisterDrawerItems.map(item => (
            <DrawerNavItem
              key={`${item.title}-${item.href}`}
              to={item.href}
              label={item.title}
              icon={item.icon}
              onClick={handleNavigate}
            />
          ))}
          <Title>Geral</Title>
          {GeneralDrawerItems.map(item => (
            <DrawerNavItem
              key={`${item.title}-${item.href}`}
              to={item.href}
              label={item.title}
              icon={item.icon}
              onClick={handleNavigate}
            />
          ))}
          <DrawerNavItem onClick={handleReload} label='Atualizar' icon={faArrowsRotate} />
          <Title>Conta</Title>
          {visibleProfileDrawerItems.map(item => (
            <DrawerNavItem
              key={`${item.title}-${item.href}`}
              to={item.href}
              label={item.title}
              icon={item.icon}
              onClick={handleNavigate}
            />
          ))}
          <DrawerNavItem onClick={handleLogout} label='Sair' icon={faRightFromBracket} />
        </Content>
      </main>
      <Footer>
        <span>Versão {version}</span>
      </Footer>
    </Container>
  )
}
